<template>
    <component
        :is="tag"
        class="cards-list quick-links"
        :class="{
            [$cms.computedBackgroundClass(display, layout?.name)]: true,
            [`cards-${cardCount}`]: true,
            [display.component]: true,
            'inline': tag !== 'section',
        }"
    >
        <div class="inner">
            <div v-if="title || isSlider" class="heading">
                <h2 v-if="title" class="h4">{{ title }}</h2>
                <div v-if="isSlider" class="arrow-controls">
                    <button
                        class="left"
                        :disabled="active < 1"
                        @click="prevSlide()"
                    >
                        <DSFontIcon
                            code="arrow-left"
                            icon-style="filled"
                            icon-set="st1website"
                        />
                    </button>
                    <button
                        class="right"
                        :disabled="!canShowNext"
                        @click="nextSlide()"
                    >
                        <DSFontIcon code="arrow-right" icon-style="filled" icon-set="st1website" />
                    </button>
                </div>
            </div>
            <component
                :is="isSlider ? 'Flicking' : 'div'"
                ref="flic"
                :options="computedOptions"
                class="card-container"
                @changed="onChanged"
            >
                <button
                    v-for="(card, index) in cards"
                    :key="index"
                    class="card"
                    draggable="false"
                    :tabindex="card.links?.[0] ? 0 : -1"
                    :class="{
                        'has-link': card.links?.[0],
                        'has-image': card.image,
                    }"
                    @click="onClick(card.links?.[0])"
                >
                    <div
                        class="content"
                    >
                        <div>
                            <h3 v-if="card.title" class="h5">{{ card.title }}</h3>
                            <DSCFText
                                v-if="card.text && typeof(card.text) === 'object'"
                                class="text"
                                :content="card.text"
                            />
                            <div v-else-if="card.text" class="text">{{ card.text }}</div>
                        </div>
                        <div v-if="card.links && card.links.length > 0" class="buttons">
                            <div
                                v-for="link, lKey in card.links?.filter((item, key) => key === 0).filter(link => link.title)"
                                :key="`link-${lKey}`"
                                class="ds-btn ds-btn--primary ds-btn--rounded"
                            >
                                <DSFontIcon
                                    code="arrow-right"
                                    size="l"
                                    icon-style="filled"
                                    icon-set="st1website"
                                />
                            </div>
                        </div>
                    </div>
                    <DSFrontifyAsset
                        v-if="card.image"
                        :asset="card.image"
                        fill="cover"
                        :class="{'icon': card.image[0].ext === 'svg'}"
                    />
                </button>
            </component>
        </div>
    </component>
</template>
<script setup>
import { ref, computed, inject, useRouter, useNuxtApp, navigateTo } from '#imports';

const { $bp, $cms } = useNuxtApp();
const layout = inject('layout');
const router = useRouter();

const props = defineProps({
    contentType: {
        type: [String, null],
        required: false,
        default: 'cardList',
    },
    internal: {
        type: [String, null],
        required: false,
        default: 'Card List',
    },
    display: {
        type: [Object],
        required: false,
        default: () => ({
            component: 'list',
            alternating: 'false',
        }),
    },
    title: {
        type: [String, null],
        required: false,
        default: '',
    },
    cards: {
        type: [Array],
        required: false,
        default: () => [],
    },
    isSchedulerEnabled: {
        type: Boolean,
        required: false,
        default: false,
    },
    createdAt: {
        type: String,
        required: false,
        default: '',
    },
    updatedAt: {
        type: String,
        required: false,
        default: '',
    },
    countOverride: {
        type: [String, Number],
        required: false,
        default: '',
    },
    tag: {
        type: String,
        required: false,
        default: 'section',
    },
});

const flic = ref(null);
const active = ref(0);

const isSlider = computed(() => cardCount.value === 'many');

const computedOptions = computed(() => {
    if (isSlider.value) {
        return {
            align: 'prev',
            circular: false,
            bound: true,
        };
    }

    return null;
});

const cardCount = computed(() => {
    if (props.countOverride) {
        return props.countOverride;
    } else if (props.display.count && props.display.count !== 'many') {
        return props.display.count;
    } else if (props.cards.length < 3) {
        return 2;
    } else if (props.cards.length < 5) {
        return props.cards.length;
    }

    return 'many';
});

const canShowNext = computed(() => {
    if ($bp.bps.isDesktop) {
        if (props.cards.length > 4 && active.value < props.cards.length - 4) {
            return true;
        }
    } else if (props.cards.length > 1 && active.value < props.cards.length - 1) {
        return true;
    }

    return false;
});

const prevSlide = () => {
    flic.value.prev();
};

const nextSlide = () => {
    flic.value.next();
};

const onChanged = e => {
    active.value = e.index;
};

const onClick = link => {
    const resolvedLink = $cms.parseLink(link);

    if (resolvedLink.to) {
        router.push(resolvedLink.to);

        return true;
    } else if (resolvedLink.href) {
        navigateTo(resolvedLink.href, { external: true });

        return true;
    }

    return false;
};

</script>
<style scoped>
.cards-list.quick-links {
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.cards-list.quick-links .inner {
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding-left: 1.5rem;
  width: 100%;
  padding-right: 1.5rem;
}
.cards-list.quick-links:not(.inline) {
  margin-top: 4rem;
  margin-bottom: 4rem;
}
.cards-list.quick-links.inline .inner {
  padding-inline: 0;
}
.cards-list.quick-links.bg-primary .card, .cards-list.quick-links.bg-secondary .card, .cards-list.quick-links.bg-custom .card, .cards-list.quick-links.bg-fullbg .card {
  padding: 1rem;
}
.cards-list.quick-links .card .ds-btn .icon {
  text-align: center;
  padding-top: 0.875rem;
}
.cards-list.quick-links:not(.bg-primary) .card .ds-btn span {
  background-color: transparent;
}
.cards-list.quick-links.bg-primary .card {
  background: var(--color-primary-500);
}
.cards-list.quick-links.bg-secondary .card {
  background: var(--color-secondary-500);
}
.cards-list.quick-links.bg-secondary .card .ds-btn span {
  background-color: transparent;
}
.cards-list.quick-links.bg-custom .card {
  background-color: v-bind("display.background");
}
.cards-list.quick-links.cards-1 .card-container {
  grid-template-columns: 1fr;
}
.cards-list.quick-links.cards-1 .card {
  grid-template-columns: 265px 1fr;
  grid-template-rows: 1fr;
  grid-gap: 0 1rem;
}
.cards-list.quick-links.cards-1:not(.full-bg) .image {
  padding-top: 93.44%;
}
.cards-list.quick-links.cards-1.full-bg .image {
  padding-top: 41.4%;
}
.cards-list.quick-links.cards-2 .card-container {
  grid-template-columns: repeat(2, 1fr);
}
.cards-list.quick-links.cards-2.full-bg .card .image {
  padding-top: 84.3%;
}
.cards-list.quick-links.cards-2:not(.full-bg) .card .image {
  padding-top: 62.5%;
}
.cards-list.quick-links.cards-3 .card-container {
  grid-template-columns: repeat(3, 1fr);
}
.cards-list.quick-links.cards-3:not(.full-bg) .card .image {
  padding-top: 84.4%;
}
.cards-list.quick-links.cards-3.full-bg .card .image {
  padding-top: 88.888%;
}
.cards-list.quick-links.cards-4 .card-container {
  grid-template-columns: repeat(4, 1fr);
}
.cards-list.quick-links.cards-4 .card {
  overflow-x: hidden;
}
.cards-list.quick-links.cards-4:not(.full-bg) .card .image, .cards-list.quick-links.cards-many:not(.full-bg) .card .image {
  padding-top: 70.4%;
}
.cards-list.quick-links.cards-4.full-bg .card .image, .cards-list.quick-links.cards-many.full-bg .card .image {
  padding-top: 120%;
}
.cards-list.quick-links.cards-4.full-bg .card .content > div:first-child, .cards-list.quick-links.cards-many.full-bg .card .content > div:first-child {
  display: inline-flex;
  flex-flow: column wrap;
  align-self: end;
}
@media (max-width: 991px) {
  .cards-list.quick-links.cards-1 .card-container, .cards-list.quick-links.cards-2 .card-container, .cards-list.quick-links.cards-3 .card-container, .cards-list.quick-links.cards-4 .card-container {
    grid-template-columns: 1fr;
  }
  .cards-list.quick-links.cards-1 .card {
    grid-template-columns: 1fr;
  }
  .cards-list.quick-links.cards-1.full-bg .card .image, .cards-list.quick-links.cards-1:not(.full-bg) .card .image, .cards-list.quick-links.cards-1 .card .image, .cards-list.quick-links.cards-2.full-bg .card .image, .cards-list.quick-links.cards-2:not(.full-bg) .card .image, .cards-list.quick-links.cards-2 .card .image, .cards-list.quick-links.cards-3.full-bg .card .image, .cards-list.quick-links.cards-3:not(.full-bg) .card .image, .cards-list.quick-links.cards-3 .card .image, .cards-list.quick-links.cards-4.full-bg .card .image, .cards-list.quick-links.cards-4:not(.full-bg) .card .image, .cards-list.quick-links.cards-4 .card .image, .cards-list.quick-links.cards-many.full-bg .card .image, .cards-list.quick-links.cards-many:not(.full-bg) .card .image, .cards-list.quick-links.cards-many .card .image, .cards-list.quick-links.cards-mosaic.full-bg .card .image, .cards-list.quick-links.cards-mosaic:not(.full-bg) .card .image, .cards-list.quick-links.cards-mosaic .card .image {
    padding-top: 67.34%;
  }
}
.cards-list.quick-links.cards-many .card-container {
  display: grid;
  grid-template-columns: 100%;
  max-width: 100%;
}
.cards-list.quick-links.cards-many .card {
  display: grid;
  min-width: calc(25% - 0.75rem);
  margin-right: 1rem;
  user-select: none;
}
@media (max-width: 1199px) {
  .cards-list.quick-links.cards-many .card {
    min-width: 100%;
  }
}
.cards-list.quick-links.full-bg .card, .cards-list.quick-links.cards-mosaic .card {
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
}
.cards-list.quick-links.full-bg .image, .cards-list.quick-links.full-bg .content, .cards-list.quick-links.cards-mosaic .image, .cards-list.quick-links.cards-mosaic .content {
  grid-column: 1/1;
  grid-row: 1/1;
}
.cards-list.quick-links.full-bg .content, .cards-list.quick-links.cards-mosaic .content {
  padding: 1.25rem;
  z-index: 1;
  color: var(--color-base-white);
  align-self: end;
  overflow: hidden;
}
.cards-list.quick-links.full-bg .buttons, .cards-list.quick-links.cards-mosaic .buttons {
  grid-row-end: span 1;
}
@media (min-width: 1200px) {
  .cards-list.quick-links.cards-mosaic .card-container {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-auto-rows: 0.25fr;
    gap: 1rem;
  }
  .cards-list.quick-links.cards-mosaic .card-container .card {
    grid-row: span 3;
  }
  .cards-list.quick-links.cards-mosaic .card-container .card.has-image {
    grid-template-rows: 1fr;
  }
  .cards-list.quick-links.cards-mosaic .card-container .card:nth-child(1) {
    grid-column: span 2;
    grid-row: span 4;
  }
  .cards-list.quick-links.cards-mosaic .card-container .card:nth-child(3) {
    grid-row: span 4;
  }
}
.cards-list.quick-links .heading {
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}
.cards-list.quick-links .heading .h4 {
  margin-bottom: 0;
}
.cards-list.quick-links .heading .arrow-controls {
  margin-left: auto;
}
.cards-list.quick-links .arrow-controls button {
  transition: all 0.2s ease-in-out;
  border-radius: 50%;
  width: 3rem;
  height: 3rem;
  background: var(--color-primary-500);
  color: var(--color-base-white);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.cards-list.quick-links .arrow-controls button:disabled {
  opacity: 0.5;
}
.cards-list.quick-links .arrow-controls button.left {
  margin-right: 0.25rem;
}
.cards-list.quick-links .card-container {
  display: grid;
  grid-gap: 1rem;
}
.cards-list.quick-links .card-container > h2 {
  grid-column-end: span 4;
}
@media (max-width: 1199px) {
  .cards-list.quick-links .card-container {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 767px) {
  .cards-list.quick-links .card-container {
    grid-template-columns: 1fr;
  }
}
.cards-list.quick-links .card {
  flex-flow: column wrap;
  margin-bottom: 0;
  height: 100%;
  display: grid;
  grid-template-rows: 1fr;
  text-align: left;
  cursor: default;
}
.cards-list.quick-links .card .image,
.cards-list.quick-links .card .video {
  padding-top: 52%;
}
.cards-list.quick-links .card .icon {
  padding: 0.5rem;
  height: 3.5rem;
  width: 3.5rem;
  border-radius: 1rem;
}
.cards-list.quick-links .card.has-link {
  cursor: pointer;
}
.cards-list.quick-links .card.has-image {
  grid-template-rows: auto 1fr;
}
.cards-list.quick-links .buttons {
  display: flex;
  align-self: end;
  justify-self: end;
  grid-column-start: 2;
  grid-row: span 2;
}
.cards-list.quick-links .buttons .ds-btn--rounded {
  display: inline-flex;
  padding: 0;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  margin-right: 0;
  transition: color 0s linear, background 0.2s ease-in-out;
}
.cards-list.quick-links .buttons .ds-btn--rounded:not(:first-child) {
  display: none;
}
.cards-list.quick-links .buttons .ds-btn--rounded .icon {
  margin: 0.5rem 0.5rem 0;
}
.cards-list.quick-links .content {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-gap: 0 0.5rem;
  order: 1;
  padding: 1rem 0 0;
}
.cards-list.quick-links .content .text {
  max-width: 100%;
  overflow-x: hidden;
}
.cards-list.quick-links .content p {
  margin-bottom: 0;
}
.cards-list.quick-links .flicking-viewport {
  height: auto;
}
@media (max-width: 1199px) {
  .cards-list.quick-links h2 {
    grid-column-end: 1;
  }
  .cards-list.quick-links .inner {
    width: 100dvw;
    grid-template-columns: 100%;
  }
  .cards-list.quick-links .inner > h2 {
    grid-column-end: 1;
  }
  .cards-list.quick-links .inner > * {
    grid-column-start: 1;
  }
  .cards-list.quick-links .inner .card:not(:last-child) {
    margin-bottom: 0;
  }
}

.cards-list.icon-cards .icon {
  background-color: var(--color-base-white, #FFF);
}
.cards-list.icon-cards.bg-secondary .ds-btn .icon {
  background-color: transparent;
}
.cards-list.icon-cards.bg-primary .card {
  transition: all 0.2s ease-in-out;
}
.cards-list.icon-cards.bg-primary .card:hover {
  background-color: var(--color-primary-200);
}
.cards-list.icon-cards.bg-primary .card:hover .ds-btn--primary.ds-btn--primary {
  background-color: var(--color-primary-500);
}
.cards-list.icon-cards.bg-primary .card:hover .content .buttons .ds-btn .icon {
  transition: all 0.2s ease-in-out;
}
.cards-list.icon-cards.bg-primary .ds-btn--primary.ds-btn--primary {
  background-color: var(--color-base-white);
}
.cards-list.icon-cards.bg-secondary .card.card.card.card {
  transition: all 0.2s ease-in-out;
}
.cards-list.icon-cards.bg-secondary .card.card.card.card .ds-btn--primary.ds-btn--primary {
  background-color: var(--color-primary-500);
}
.cards-list.icon-cards.bg-secondary .card.card.card.card:hover {
  background-color: var(--color-primary-200);
}
.cards-list.icon-cards.bg-secondary .card.card.card.card:hover .content .buttons .ds-btn .icon {
  transition: all 0.2s ease-in-out;
  background: transparent;
}

:deep(.flicking-camera) {
  display: flex;
  flex-flow: row nowrap;
  height: auto;
}
</style>
